import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import theme from './HeaderSearch.module.scss';
import { classnames } from '@/utils/html';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { globalSearch, SearchResult } from '@/services/search';
import { CustomLink } from '@/components/navigation';
import Autosuggest from 'react-autosuggest';
import { useHistory } from 'react-router-dom';
import { faSearch, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { getProjectHeaderImageThumbnailUrl } from '@/services/projects';
import { useDebounce, usePrevious } from '@/utils/hooks';
import { getUserAvatarThumbnailUrl } from '@/services/users';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';

interface HeaderSearchProps {
    className?: string;
}

const HeaderSearch: React.FC<HeaderSearchProps> = ({ className }) => {
    const projectId = useSelector((state: IState) => state.global.activeProject?.id);
    const history = useHistory();

    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [resultsSearchText, setResultsSearchText] = useState('');

    const inputEl = useRef<HTMLInputElement | null>(null);
    const activeRequestRef = useRef<Promise<SearchResult[]> | null>(null);

    const debouncedText = useDebounce(searchText, 300);

    const doSearch = useCallback(
        (value) => {
            activeRequestRef.current = globalSearch(projectId!, value);

            activeRequestRef.current.then((result) => {
                if (value === searchText) {
                    setSearchResults(result);
                    setResultsSearchText(value);
                }
            });
        },
        [searchText, projectId]
    );

    const previousDebouncedText = usePrevious(debouncedText);
    useEffect(() => {
        if (previousDebouncedText !== undefined && debouncedText !== previousDebouncedText) {
            doSearch(debouncedText);
        }
    }, [debouncedText, previousDebouncedText, doSearch]);

    const getItemLink = (searchResult: SearchResult) => {
        const prefix = ``;
        switch (searchResult.objectType) {
            // case 'Project':
            //     return `${prefix}/projects/${searchResult.objectId}`;
            case 'ResidentialUnit':
                return `${prefix}/projects/${searchResult.projectId}/residential-units/${searchResult.objectId}`;
            // case 'CatalogProduct':
            //     return `${prefix}/catalog/products/${searchResult.objectId}`;
            case 'User':
                return `${prefix}/projects/${searchResult.projectId}/residential-units/${searchResult.objectInfo}`;
            default:
                return prefix;
        }
    };

    const getObjectTypeName = (searchResult: SearchResult) => {
        switch (searchResult.objectType) {
            // case 'Project':
            //     return <Trans>Project</Trans>;
            case 'ResidentialUnit':
                return <Trans>Residential unit</Trans>;
            // case 'CatalogProduct':
            //     return <Trans>Product</Trans>;
            case 'User':
                return <Trans>User</Trans>;
            default:
                return <Trans>Unknown</Trans>;
        }
    };

    const handleSearchChange = (e, { newValue }) => {
        setSearchText(newValue);
    };

    const onSuggestionsFetchRequested = ({ value, reason }) => {
        if (reason === 'input-focused') {
            doSearch(value);
        }

        // else {
        //     debouncedSearch(value);
        // }
    };

    const onSuggestionsClearRequested = () => {
        //TODO:Fix fetch abort
        // if(this.activeRequest) {
        //     this.activeRequest.abort();
        // }
        setSearchResults([]);
    };

    const handleSuggestionSelected = (e, { method, suggestion }) => {
        if (method === 'enter') {
            history.push(getItemLink(suggestion));
        }

        inputEl.current && inputEl.current.blur();
    };

    const getImageUrl = (searchResult: SearchResult) => {
        switch (searchResult.objectType) {
            // case 'Project':
            //     return getProjectHeaderImageThumbnailUrl(searchResult.projectId);
            case 'ResidentialUnit':
                return getProjectHeaderImageThumbnailUrl(searchResult.projectId);
            // case 'CatalogProduct':
            //     return getCatalogProductImageThumbnailUrl(searchResult.imageId!);
            case 'User':
                return getUserAvatarThumbnailUrl(searchResult.objectId);
            default:
                return null;
        }
    };

    const getInfoText = (searchResult: SearchResult) => {
        switch (searchResult.objectType) {
            // case 'Project':
            //     return (
            //         <>
            //             {searchResult.objectInfo} <Trans>Units</Trans>
            //         </>
            //     );
            case 'ResidentialUnit':
                return searchResult.objectInfo ? <Trans>Sold</Trans> : <Trans id="Available.Plural">Available</Trans>;
            // case 'CatalogProduct':
            //     return <ItemCategoryDisplay type="root" value={searchResult.objectInfo} />;
            case 'User':
                return searchResult.residentialUnitName;
            default:
                return null;
        }
    };
    const getImageStyle = (searchResult: SearchResult) => {
        switch (searchResult.objectType) {
            // case 'Project':
            case 'User':
            case 'ResidentialUnit':
                return 'circle';
            default:
                return 'square';
        }
    };

    const renderSuggestion = (suggestion) => {
        const infoText = getInfoText(suggestion);
        const imageUrl = getImageUrl(suggestion);

        const imageStyle = getImageStyle(suggestion);

        // return (
        //     <HeaderDropdownInternalLinkItem
        //         to={getItemLink(suggestion)}
        //         imageElement={imageUrl ? <Thumbnail src={imageUrl} /> : undefined}
        //         title={suggestion.displayName}
        //         subtitle={getObjectTypeName(suggestion)}
        //     />
        // );

        return (
            <CustomLink to={getItemLink(suggestion)}>
                <div className={classnames(theme.suggestionImage, { [theme.circle]: imageStyle === 'circle' })}>
                    {imageUrl && <Thumbnail src={imageUrl} />}
                </div>
                <div>
                    <div className={theme.suggestionName}>{suggestion.displayName}</div>
                    <div className={theme.suggestionType}>{getObjectTypeName(suggestion)}</div>
                </div>
                <div className={theme.suggestionInfo}>{infoText}</div>
            </CustomLink>
        );
    };

    //TODO: Implement (Type “H101” or “Goaleitet”) in placeholder

    const loading = searchText && resultsSearchText !== searchText;

    if (!projectId) {
        return null;
    }
    const inputProps = {
        placeholder: t`Search...`,
        value: searchText,
        onChange: handleSearchChange,
        type: 'search',
    };
    return (
        <div className={classnames(theme.searchBox, className, { [theme.loading]: loading })}>
            <Autosuggest
                theme={theme}
                suggestions={searchResults}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                getSuggestionValue={(suggestion) => searchText}
                renderSuggestion={renderSuggestion}
                inputProps={inputProps}
                focusInputOnSuggestionClick={false}
                onSuggestionSelected={handleSuggestionSelected}
                highlightFirstSuggestion
                ref={(autosuggest) => {
                    if (autosuggest) {
                        inputEl.current = (autosuggest as any).input;
                    }
                }}
            />
            <div className={theme.loadingIndicator}>
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
            <div className={theme.searchIcon}>
                <FontAwesomeIcon icon={faSearch} />
            </div>
        </div>
    );
};

export default HeaderSearch;
